import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Tab,
  Tabs,
  Badge,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import RuleIcon from '@mui/icons-material/Rule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import { alertsService, AlertsDashboard as AlertsDashboardType, Alert as AlertType, AlertRule } from '../api/services/alerts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AlertsManagement: React.FC = () => {
  const [dashboard, setDashboard] = useState<AlertsDashboardType | null>(null);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardData, alertsData, rulesData] = await Promise.all([
        alertsService.getDashboard(30),
        alertsService.getAlerts({ limit: 100 }),
        alertsService.getRules({ limit: 100 }),
      ]);

      setDashboard(dashboardData);
      setAlerts(alertsData.alerts);
      setRules(rulesData.rules);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load alerts data');
      console.error('Error fetching alerts data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return <ErrorIcon />;
      case 'high':
        return <WarningIcon />;
      case 'medium':
        return <NotificationsActiveIcon />;
      case 'low':
        return <CheckCircleIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboard) return null;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Badge badgeContent={dashboard.dashboard.alerts.active} color="error">
            <NotificationsActiveIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          </Badge>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Alerts Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor and manage business intelligence alerts
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* TODO: Open create rule dialog */}}
          >
            New Alert Rule
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <NotificationsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.alerts.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Alerts
                  </Typography>
                  <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                    <Chip label={`${dashboard.dashboard.alerts.active} Active`} size="small" color="error" />
                    <Chip label={`${dashboard.dashboard.alerts.resolved} Resolved`} size="small" color="success" />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.alerts.critical}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Critical Alerts
                  </Typography>
                  <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                    <Chip label={`${dashboard.dashboard.alerts.high} High`} size="small" color="warning" />
                    <Chip label={`${dashboard.dashboard.alerts.medium} Medium`} size="small" color="info" />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <RuleIcon sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.alert_rules.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Alert Rules
                  </Typography>
                  <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                    <Chip label={`${dashboard.dashboard.alert_rules.enabled} Enabled`} size="small" color="success" />
                    <Chip label={`${dashboard.dashboard.alert_rules.disabled} Disabled`} size="small" color="default" />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.response_metrics.acknowledgement_rate.toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Acknowledgement Rate
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Avg Response: {dashboard.dashboard.response_metrics.average_response_time_minutes.toFixed(0)} min
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alert Categories */}
      {Object.keys(dashboard.dashboard.categories).length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Alerts by Category
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(dashboard.dashboard.categories).map(([category, count]) => (
                <Grid item key={category}>
                  <Chip
                    label={`${category}: ${count}`}
                    color="primary"
                    variant="outlined"
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Card>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab
            label={
              <Badge badgeContent={dashboard.dashboard.alerts.active} color="error">
                Active Alerts
              </Badge>
            }
          />
          <Tab label="All Alerts" />
          <Tab label="Alert Rules" />
          <Tab label="Statistics" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>Active Alerts</Typography>
          <Typography color="text.secondary">Active alerts table coming soon...</Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>All Alerts</Typography>
          <Typography color="text.secondary">All alerts table coming soon...</Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Alert Rules</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {/* TODO: Open create rule dialog */}}
            >
              Create Rule
            </Button>
          </Box>
          <Typography color="text.secondary">Alert rules table coming soon...</Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>Alert Statistics</Typography>
          <Typography color="text.secondary">Statistics charts coming soon...</Typography>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default AlertsManagement;

